import { useObservable, useSubscription } from '@fullstory/react-rxjs';
import { SeekResult } from '@fullstory/renderer-shared';
import { breakable, CancelAsync } from '@fullstory/util/promise';
import { useEffect } from 'react';
import { combineLatest, map, Observable } from 'rxjs';
import type { RendererClient } from './createRendererClient';

export type { SeekResult } from '@fullstory/renderer-shared';

export type SeekEndResult =
  | { succeeded: true; canceled: false; error: undefined; result: SeekResult }
  | { succeeded: false; canceled: true; error: undefined; result: undefined }
  | { succeeded: false; canceled: false; error: Error; result: undefined };

interface UsePlaybackParams {
  client?: RendererClient;
  isSuspended?: boolean;
  offsetMs$: Observable<number | undefined>;
}

interface UsePlaybackOptions {
  onSeek?: (result: SeekResult) => void;
  onSeekEnd?: (result: SeekEndResult) => void;
  onSeekError?: (e: Error) => void;
  onSeekStart?: (offsetMs: number) => void;
}

/**
 * This hook manages 3 playback side effects:
 * 1. Initialization -- play each new page
 * 2. Seeking -- synchronize with time changes
 * 3. Trailing events -- pull in trailing events when enabled
 */
export const usePlayback = (params: UsePlaybackParams, options: UsePlaybackOptions = {}) => {
  const { client, isSuspended = false, offsetMs$ } = params;
  const { onSeek, onSeekEnd, onSeekError, onSeekStart } = options;

  const offsetAndClient$ = useObservable(
    source$ =>
      combineLatest([source$, offsetMs$]).pipe(
        map(([[client], offsetMs]) => ({ client, offsetMs })),
      ),
    [client],
  );

  // --------------- Seeking ----------------
  // This effect manages the seeking behavior.
  //
  // Seeking should occur whenever the offsetMs$ Observable emits
  // or the isSuspended param changes. It is safe to access other
  // params via closure, as long as changes to those params do
  // not require the effect to re-run.
  useSubscription(offsetAndClient$, ({ offsetMs = 0, client = undefined }) => {
    // Return early if necessary
    if (!client) return;

    // Notify that seeking has started
    onSeekStart?.(offsetMs);

    // Play to the requested time and notify of result
    const work = breakable(
      client
        .seek(offsetMs)
        .then(result => {
          onSeek?.(result);
          onSeekEnd?.({ succeeded: true, canceled: false, result, error: undefined });
        })
        .catch(e => {
          if (e instanceof CancelAsync || e.isCancelAsync) {
            onSeekEnd?.({ succeeded: false, canceled: true, error: undefined, result: undefined });
            return;
          }
          onSeekError?.(e);
          onSeekEnd?.({ succeeded: false, error: e, canceled: false, result: undefined });
        }),
    );

    // Cleanup function
    return () => {
      work.cancel();
    };
  });

  // ----------------- Suspending & Resuming -------------------
  // This effect suspends or resumes the client when the isSuspended param changes.
  useEffect(() => {
    // No client, do nothing
    if (!client) return;

    // Client is already in-sync, do nothing
    if (isSuspended === client.isSuspended) return;

    // Suspend or resume the client
    if (isSuspended) {
      client.suspend();
    } else {
      client.resume();
    }
  }, [client, isSuspended]);
};
